// ClauseApi - API service for clause navigation and metadata
// Task 50.1: Clause Navigation and Deep-Linking System

import apiService from './api';
import { ClauseInfo, DocumentContext, ChunkClauseMapping } from './clauseRegistryService';

// Re-export types for external usage
export type { ClauseInfo, DocumentContext, ChunkClauseMapping };

/**
 * API endpoints for clause navigation and enhanced document context
 * 
 * This service handles API communication for clause-related operations,
 * providing the backend integration needed for enhanced chunk context display.
 */
export class ClauseApiService {
  
  /**
   * Get clause information by clause_id from backend
   */
  async getClause(clauseId: string): Promise<ClauseInfo | null> {
    try {
      const response = await apiService.get(`/api/v1/clauses/${clauseId}`);
      return response;
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn(`Clause not found: ${clauseId}`);
        return null;
      }
      console.error('Failed to fetch clause:', error);
      throw new Error(`Failed to fetch clause: ${error.message}`);
    }
  }

  /**
   * Get all clauses for a document
   */
  async getDocumentClauses(documentId: string): Promise<ClauseInfo[]> {
    try {
      const response = await apiService.get(`/api/v1/documents/${documentId}/clauses`);
      return response.clauses || [];
    } catch (error: any) {
      if (error.response?.status === 404) {
        console.warn(`Document not found: ${documentId}`);
        throw new Error(`Document not found (404): ${documentId}`);
      }
      console.error('Failed to fetch document clauses:', error);
      throw new Error(`Failed to fetch document clauses: ${error.message}`);
    }
  }

  /**
   * Get clause hierarchy for a document (structured tree)
   */
  async getClauseHierarchy(documentId: string): Promise<ClauseInfo[]> {
    try {
      const response = await apiService.get(`/api/v1/documents/${documentId}/clause-hierarchy`);
      return response.hierarchy || [];
    } catch (error: any) {
      console.error('Failed to fetch clause hierarchy:', error);
      // Fallback to flat clause list
      return this.getDocumentClauses(documentId);
    }
  }

  /**
   * Get chunk-to-clause mappings for enhanced context
   */
  async getChunkMappings(documentId: string): Promise<ChunkClauseMapping[]> {
    try {
      const response = await apiService.get(`/api/v1/documents/${documentId}/chunk-mappings`);
      return response.mappings || [];
    } catch (error: any) {
      console.error('Failed to fetch chunk mappings:', error);
      return [];
    }
  }

  /**
   * Get enhanced context for a specific chunk
   */
  async getChunkContext(
    documentId: string, 
    chunkIndex: number
  ): Promise<{ 
    documentContext: DocumentContext;
    clauseInfo: ClauseInfo[];
  } | null> {
    try {
      const response = await apiService.get(`/api/v1/documents/${documentId}/chunks/${chunkIndex}/context`);
      return {
        documentContext: response.document_context,
        clauseInfo: response.clause_info || [],
      };
    } catch (error: any) {
      console.error('Failed to fetch chunk context:', error);
      return null;
    }
  }

  /**
   * Search clauses by content or logical_id
   */
  async searchClauses(documentId: string, query: string): Promise<ClauseInfo[]> {
    try {
      const response = await apiService.get(`/api/v1/documents/${documentId}/clauses/search?q=${encodeURIComponent(query)}`);
      return response.clauses || [];
    } catch (error: any) {
      console.error('Failed to search clauses:', error);
      return [];
    }
  }

  /**
   * Get clause navigation data (breadcrumbs, siblings, children)
   */
  async getClauseNavigation(clauseId: string): Promise<{
    breadcrumbs: ClauseInfo[];
    siblings: ClauseInfo[];
    children: ClauseInfo[];
  } | null> {
    try {
      const response = await apiService.get(`/api/v1/clauses/${clauseId}/navigation`);
      return {
        breadcrumbs: response.breadcrumbs || [],
        siblings: response.siblings || [],
        children: response.children || [],
      };
    } catch (error: any) {
      console.error('Failed to fetch clause navigation:', error);
      return null;
    }
  }

  /**
   * Check if a document supports clause navigation
   */
  async hasClauseSupport(documentId: string): Promise<boolean> {
    try {
      const response = await apiService.get(`/api/v1/documents/${documentId}/clause-support`);
      return response.supported || false;
    } catch (error: any) {
      console.error('Failed to check clause support:', error);
      return false;
    }
  }
}

// Export singleton instance
export const clauseApiService = new ClauseApiService();

/**
 * Enhanced issue retrieval with clause context
 */
export const getIssuesWithClauseContext = async (projectId: string) => {
  try {
    const response = await apiService.get(`/api/v1/projects/${projectId}/issues/enhanced`);
    return response;
  } catch (error: any) {
    console.error('Failed to fetch issues with clause context:', error);
    // Fallback to standard issue retrieval
    const fallbackResponse = await apiService.get(`/api/v1/projects/${projectId}/issues`);
    return fallbackResponse.data;
  }
};

/**
 * Update issue with clause references
 */
export const updateIssueClauseReferences = async (
  issueId: string, 
  clauseIds: string[]
) => {
  try {
    const response = await apiService.patch(`/api/v1/issues/${issueId}/clause-references`, {
      clause_ids: clauseIds
    });
    return response;
  } catch (error: any) {
    console.error('Failed to update issue clause references:', error);
    throw error;
  }
};

/**
 * Get clause statistics for a document (for performance dashboards)
 */
export const getClauseStatistics = async (documentId: string) => {
  try {
    const response = await apiService.get(`/api/v1/documents/${documentId}/clause-stats`);
    return response;
  } catch (error: any) {
    console.error('Failed to fetch clause statistics:', error);
    return {
      total_clauses: 0,
      clause_types: {},
      max_depth: 0,
      coverage_percentage: 0
    };
  }
};

// Deep-linking and navigation helper methods for Task 50.1
export const clauseNavigationHelpers = {
  /**
   * Parse clause_id from URL format or direct ID
   */
  parseClauseId(clauseIdOrUrl: string): string {
    // Handle URL format: /documents/:documentId/clause/:clauseId
    const urlMatch = clauseIdOrUrl.match(/\/clause\/([^\/\?#]+)/);
    if (urlMatch) {
      return decodeURIComponent(urlMatch[1]);
    }
    
    // Direct clause_id
    return clauseIdOrUrl;
  },

  /**
   * Generate deep-link URL for a clause
   */
  generateClauseUrl(documentId: string, clauseId: string): string {
    const encodedClauseId = encodeURIComponent(clauseId);
    return `/documents/${documentId}/clause/${encodedClauseId}`;
  },

  /**
   * Build clause hierarchy tree from flat list
   */
  buildHierarchyTree(clauses: ClauseInfo[]): (ClauseInfo & { children?: ClauseInfo[] })[] {
    // Create a map for quick parent lookups
    const clauseMap = new Map<string, ClauseInfo & { children?: ClauseInfo[] }>();
    clauses.forEach(clause => {
      clauseMap.set(clause.logical_id, { ...clause, children: [] });
    });

    const rootClauses: (ClauseInfo & { children?: ClauseInfo[] })[] = [];

    // Build the hierarchy
    clauses.forEach(clause => {
      const clauseWithChildren = clauseMap.get(clause.logical_id)!;
      
      if (clause.parent_logical_id) {
        const parent = clauseMap.get(clause.parent_logical_id);
        if (parent) {
          parent.children!.push(clauseWithChildren);
        } else {
          // Parent not found, treat as root
          rootClauses.push(clauseWithChildren);
        }
      } else {
        // Root level clause
        rootClauses.push(clauseWithChildren);
      }
    });

    // Sort clauses at each level by logical_id
    const sortClauses = (clauses: (ClauseInfo & { children?: ClauseInfo[] })[]): void => {
      clauses.sort((a, b) => a.logical_id.localeCompare(b.logical_id, undefined, { numeric: true }));
      clauses.forEach(clause => {
        if (clause.children && clause.children.length > 0) {
          sortClauses(clause.children);
        }
      });
    };

    sortClauses(rootClauses);
    return rootClauses;
  },

  /**
   * Generate breadcrumb trail for a clause
   */
  generateBreadcrumbs(clause: ClauseInfo, allClauses: ClauseInfo[]): ClauseInfo[] {
    const breadcrumbs: ClauseInfo[] = [];
    let currentClause: ClauseInfo | undefined = clause;
    
    // Build breadcrumb chain from current clause up to root
    while (currentClause) {
      breadcrumbs.unshift(currentClause);
      
      if (currentClause.parent_logical_id) {
        currentClause = allClauses.find(c => c.logical_id === currentClause!.parent_logical_id);
      } else {
        currentClause = undefined;
      }
    }
    
    return breadcrumbs;
  },

  /**
   * Validate clause_id format
   */
  isValidClauseId(clauseId: string): boolean {
    // Clause ID format: {doc_id}:{logical_id}:{revision_id}
    const parts = clauseId.split(':');
    if (parts.length !== 3) return false;
    
    const [docId, logicalId, revisionId] = parts;
    
    // Basic validation
    if (!docId || docId.length !== 10) return false;
    if (!logicalId) return false;
    if (!revisionId || revisionId.length !== 10) return false;
    
    // Check that revision_id looks like hex
    return /^[a-fA-F0-9]+$/.test(revisionId);
  },

  /**
   * Format clause type for display
   */
  formatClauseType(clause: ClauseInfo): string {
    const types = [];
    if (clause.is_definition) types.push('Definition');
    if (clause.is_schedule) types.push('Schedule'); 
    if (clause.is_table) types.push('Table');
    return types.length > 0 ? types.join(', ') : 'Standard Clause';
  },

  /**
   * Format page range for display
   */
  formatPageRange(clause: ClauseInfo): string {
    if (!clause.page_start) return '';
    if (clause.page_start === clause.page_end) return `Page ${clause.page_start}`;
    return `Pages ${clause.page_start}-${clause.page_end || '?'}`;
  }
};

// Type definitions for API responses
export interface ClauseSearchResult {
  clause: ClauseInfo;
  relevance_score: number;
  context_snippet: string;
}

export interface ClauseStatistics {
  total_clauses: number;
  clause_types: Record<string, number>;
  max_depth: number;
  coverage_percentage: number;
  definitions_count: number;
  schedules_count: number;
  tables_count: number;
}

// Navigation-specific type definitions for Task 50.1
export interface ClauseNavigation {
  breadcrumbs: ClauseInfo[];
  siblings: ClauseInfo[];
  children: ClauseInfo[];
}

export interface ClauseHierarchyNode extends ClauseInfo {
  children?: ClauseHierarchyNode[];
}